/**
 * OneDrive download URL API slice. State: loading, isError, message, downloadUrl.
 * Thunk only; no legacy action dispatches.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getOneDriveDownloadUrl } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import { DOC_CHECK_IN_TYPE } from "../../constants/metaData";

const userAuth = new UserAuth();
const DOWNLOAD_DIALOG_AUTO_CLOSE_MS = 3000;
const CHECKIN_NEW_OPERATION = DOC_CHECK_IN_TYPE.checkinNew;
const CHECKIN_NEW_REVISION_OPERATION = DOC_CHECK_IN_TYPE.checkinNewRevision;

function isInternalCheckinDownload(operationType) {
  return (
    operationType === CHECKIN_NEW_OPERATION ||
    operationType === CHECKIN_NEW_REVISION_OPERATION
  );
}

export const fetchOneDriveDownloadUrl = createAsyncThunk(
  "oneDriveDownload/fetch",
  async (
    {
      teamId,
      channelId,
      tenantId,
      documentId,
      fileRoot,
      operationType = DOC_CHECK_IN_TYPE.checkinNew,
    },
    { rejectWithValue, dispatch }
  ) => {
    const closeDialogAfterDelay = () => {
      setTimeout(() => {
        dispatch(setDownloadDialogOpen(false));
      }, DOWNLOAD_DIALOG_AUTO_CLOSE_MS);
    };

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("fetchOneDriveDownloadUrl no access token", tokenResponse, "REDUX");
      closeDialogAfterDelay();
      return rejectWithValue({ message: "No access token" });
    }
    try {
      const { data, status } = await getOneDriveDownloadUrl(accessToken, {
        teamId,
        channelId,
        tenantId,
        documentId,
        oneDriveRoot: fileRoot ?? "",
      });
      if (status === 200) {
        const raw = data?.value != null ? data.value : data;
        const url = typeof raw === "string" ? raw : "";
        closeDialogAfterDelay();
        return url;
      }
      closeDialogAfterDelay();
      return rejectWithValue({ message: `Request failed (${status})` });
    } catch (err) {
      logger.error("fetchOneDriveDownloadUrl failed", err, "REDUX");
      const message =
        err?.response?.data?.message ?? err?.message ?? "Failed to get download URL";
      closeDialogAfterDelay();
      return rejectWithValue({ message });
    }
  }
);

const initialState = {
  loading: false,
  isError: false,
  message: "",
  downloadUrl: "",
  isOpen: false,
};

const oneDriveDownloadSlice = createSlice({
  name: "oneDriveDownload",
  initialState,
  reducers: {
    clearOneDriveDownloadUrl: (state) => {
      state.downloadUrl = "";
      state.isError = false;
      state.message = "";
    },
    setDownloadDialogOpen: (state, action) => {
      state.isOpen = action.payload ?? false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOneDriveDownloadUrl.pending, (state, action) => {
        const isInternalCheckinFlow = isInternalCheckinDownload(
          action.meta?.arg?.operationType,
        );
        state.loading = true;
        state.isError = false;
        state.message = isInternalCheckinFlow ? "" : "Downloading document...";
        state.isOpen = isInternalCheckinFlow ? false : true;
      })
      .addCase(fetchOneDriveDownloadUrl.fulfilled, (state, action) => {
        const isInternalCheckinFlow = isInternalCheckinDownload(
          action.meta?.arg?.operationType,
        );
        state.loading = false;
        state.isError = false;
        state.message = isInternalCheckinFlow
          ? ""
          : "Document downloaded successfully!";
        state.downloadUrl = action.payload ?? "";
      })
      .addCase(fetchOneDriveDownloadUrl.rejected, (state, action) => {
        const isInternalCheckinFlow = isInternalCheckinDownload(
          action.meta?.arg?.operationType,
        );
        state.loading = false;
        state.isError = true;
        state.message = isInternalCheckinFlow
          ? ""
          : action.payload?.message ?? "Unknown error";
        state.downloadUrl = "";
      });
  },
});

export const { clearOneDriveDownloadUrl, setDownloadDialogOpen } = oneDriveDownloadSlice.actions;
export default oneDriveDownloadSlice.reducer;
